/**
 * metadata 기반 셀 identity.
 *
 * data-cell-id는 렌더/저장 경계용 식별자이고 unique하지 않을 수 있다.
 * 동일 값 그룹은 백엔드 metadata 9개 필드로 만든 identityKey로 판정한다.
 */

import type {
  IxdChunk,
  IxdDlView,
  PeriodType,
  TableColumn,
  TableRow,
} from "@/apis/ixd-editor/types";
import {
  buildCellIdAliasesFromTaggedValue,
  buildCellIdFromTableColumn,
  buildCellIdLookupCandidates,
  type CellIdentityKey,
  isValueCell,
  makeCellIdentityKey,
  normalizeCellId,
} from "./cell-index";
import {
  buildTableLabelMap,
  createTableSemanticResolver,
} from "./table-semantics";
export type { CellIdentityKey } from "./cell-index";
export { makeCellIdentityKey } from "./cell-index";

export type NormalizedCellId = string;

export interface CellIdentityIndex {
  /** normalizedCellId -> metadata identity key */
  identityKeyByNormalizedCellId: Map<NormalizedCellId, CellIdentityKey>;
  /** identity key -> 동일 identity를 가진 normalizedCellId 목록 */
  normalizedCellIdsByIdentityKey: Map<CellIdentityKey, Set<NormalizedCellId>>;
  /** identity key -> 동일 identity value가 나타나는 보고서 위치 목록 */
  identityLocationsByKey: Map<CellIdentityKey, CellIdentityLocation[]>;
}

export interface CellIdentityLocation {
  identityKey: CellIdentityKey;
  uid?: string;
  normalizedCellId: NormalizedCellId;
  sourceCellId?: string;
  chunkId: string;
  chunkTitle: string;
  dlViewId: string | null;
  dlViewTitle: string;
  dlViewIndex: number;
  periodType: PeriodType;
  rowIndex?: number;
  colIndex?: number;
  rowLabel: string;
  axisLabels: string[];
  value: string;
  unitText: string | null;
  schemaType?: string | null;
}

interface TableLocationDraft {
  uid: string;
  normalizedCellId: NormalizedCellId;
  sourceCellId?: string;
  rowIndex: number;
  colIndex: number;
  rowLabel: string;
  axisLabels: string[];
  value: string;
  schemaType: string;
}

const getCellIdentityLocationKey = (location: CellIdentityLocation): string =>
  location.uid ??
  [
    location.chunkId,
    location.dlViewIndex,
    location.periodType,
    location.normalizedCellId,
    location.rowIndex ?? "",
    location.colIndex ?? "",
  ].join(":");

const getActiveTable = (
  dlView: IxdDlView,
): Record<PeriodType, TableRow[]> | null => dlView.table ?? null;

const buildChunkLabelMap = (chunk: IxdChunk): Map<string, string> => {
  const map = new Map<string, string>();
  for (const dlView of chunk.dlViews) {
    const table = getActiveTable(dlView);
    if (!table) continue;
    for (const rows of Object.values(table)) {
      for (const [id, label] of buildTableLabelMap(rows)) {
        if (!map.has(id)) map.set(id, label);
      }
    }
  }
  return map;
};

const buildTableLocationDrafts = (
  chunk: IxdChunk,
  dlView: IxdDlView,
  dlViewIndex: number,
): Map<NormalizedCellId, TableLocationDraft> => {
  const table = getActiveTable(dlView);
  const map = new Map<NormalizedCellId, TableLocationDraft>();
  if (!table) return map;

  const isNote = chunk.id.startsWith("note-");
  const periodTypes = (Object.keys(table) as PeriodType[]).filter(
    (periodType) => !isNote || periodType !== "BEFORE_PREVIOUS",
  );

  for (const periodType of periodTypes) {
    const rows = table[periodType] ?? [];
    const labelMap = buildTableLabelMap(rows);
    const semanticResolver = createTableSemanticResolver({
      rows,
      periodType,
      isTransposed: dlView.isTransposed,
      labelMap,
    });
    rows.forEach((row, rowIndex) => {
      row.columns.forEach((column: TableColumn, colIndex) => {
        if (!isValueCell(column)) return;
        const canonicalCellId = buildCellIdFromTableColumn(periodType, column);
        const candidates = buildCellIdLookupCandidates(
          periodType,
          canonicalCellId,
          column.id,
          column.attributes.contextKey,
        );
        const normalizedCellId = candidates[0];
        if (!normalizedCellId) return;
        const semanticContext = semanticResolver({
          rowIndex,
          colIndex,
          column,
        });

        const draft: TableLocationDraft = {
          uid: `${chunk.id}-dl-${dlViewIndex}-${periodType}-r${rowIndex}-c${colIndex}`,
          normalizedCellId,
          sourceCellId: column.id,
          rowIndex,
          colIndex,
          rowLabel: semanticContext.rowLabel,
          axisLabels: semanticContext.axisLabels,
          value: column.text,
          schemaType: column.schemaType,
        };
        for (const candidate of candidates) {
          if (!map.has(candidate)) map.set(candidate, draft);
        }
      });
    });
  }

  return map;
};

export const buildCellIdentityMapFromValues = (
  values: IxdDlView["values"] | null | undefined,
): Map<NormalizedCellId, CellIdentityKey> => {
  const map = new Map<NormalizedCellId, CellIdentityKey>();
  if (!values) return map;

  for (const [periodType, taggedValues] of Object.entries(values)) {
    for (const tv of taggedValues) {
      const identityKey = makeCellIdentityKey(tv.metadata);
      for (const cellId of buildCellIdAliasesFromTaggedValue(periodType, tv)) {
        map.set(cellId, identityKey);
      }
    }
  }
  return map;
};

/** tagged value metadata의 schema type을 렌더 셀 alias별로 조회한다. */
export const buildCellSchemaTypeMapFromValues = (
  values: IxdDlView["values"] | null | undefined,
): Map<NormalizedCellId, string> => {
  const map = new Map<NormalizedCellId, string>();
  if (!values) return map;

  for (const [periodType, taggedValues] of Object.entries(values)) {
    for (const taggedValue of taggedValues) {
      const schemaType = taggedValue.metadata.schemaType?.trim();
      if (!schemaType) continue;
      for (const cellId of buildCellIdAliasesFromTaggedValue(
        periodType,
        taggedValue,
      )) {
        map.set(cellId, schemaType);
      }
    }
  }
  return map;
};

export const buildCellIdentityIndex = (
  chunks: IxdChunk[],
): CellIdentityIndex => {
  const identityKeyByNormalizedCellId = new Map<
    NormalizedCellId,
    CellIdentityKey
  >();
  const normalizedCellIdsByIdentityKey = new Map<
    CellIdentityKey,
    Set<NormalizedCellId>
  >();
  const identityLocationsByKey = new Map<
    CellIdentityKey,
    CellIdentityLocation[]
  >();
  const identityLocationKeysByKey = new Map<CellIdentityKey, Set<string>>();

  const addCellId = (
    normalizedCellId: NormalizedCellId,
    identityKey: CellIdentityKey,
  ) => {
    identityKeyByNormalizedCellId.set(normalizedCellId, identityKey);
    const ids = normalizedCellIdsByIdentityKey.get(identityKey) ?? new Set();
    ids.add(normalizedCellId);
    normalizedCellIdsByIdentityKey.set(identityKey, ids);
  };

  const addLocation = (
    identityKey: CellIdentityKey,
    location: CellIdentityLocation,
  ) => {
    const locationKey = getCellIdentityLocationKey(location);
    const locationKeys =
      identityLocationKeysByKey.get(identityKey) ?? new Set();
    if (locationKeys.has(locationKey)) return;
    locationKeys.add(locationKey);
    identityLocationKeysByKey.set(identityKey, locationKeys);

    const locations = identityLocationsByKey.get(identityKey) ?? [];
    locations.push(location);
    identityLocationsByKey.set(identityKey, locations);
  };

  for (const chunk of chunks) {
    const chunkLabelMap = buildChunkLabelMap(chunk);
    for (const [dlViewIndex, dlView] of chunk.dlViews.entries()) {
      const tableLocationDrafts = buildTableLocationDrafts(
        chunk,
        dlView,
        dlViewIndex,
      );
      if (dlView.values) {
        for (const [periodType, taggedValues] of Object.entries(
          dlView.values,
        )) {
          for (const tv of taggedValues) {
            const identityKey = makeCellIdentityKey(tv.metadata);
            const aliases = buildCellIdAliasesFromTaggedValue(periodType, tv);
            const normalizedCellId = aliases[0];
            for (const cellId of aliases) {
              addCellId(cellId, identityKey);
            }
            const tableLocation = aliases
              .map((cellId) => tableLocationDrafts.get(cellId))
              .find(Boolean);
            const contentUid =
              !tableLocation &&
              !dlView.table &&
              dlView.content !== null &&
              dlView.content !== undefined &&
              String(dlView.content) === String(tv.value)
                ? `${chunk.id}-dl-${dlViewIndex}-content`
                : undefined;
            if (!tableLocation && !contentUid) {
              continue;
            }
            if (tableLocation) {
              addCellId(tableLocation.normalizedCellId, identityKey);
              if (tableLocation.sourceCellId) {
                addCellId(
                  normalizeCellId(tableLocation.sourceCellId),
                  identityKey,
                );
              }
            }
            addLocation(identityKey, {
              identityKey,
              uid: tableLocation?.uid ?? contentUid,
              normalizedCellId:
                tableLocation?.normalizedCellId ?? normalizedCellId,
              sourceCellId: tableLocation?.sourceCellId,
              chunkId: chunk.id,
              chunkTitle: chunk.title,
              dlViewId: dlView.id,
              dlViewTitle: dlView.title,
              dlViewIndex,
              periodType: periodType as PeriodType,
              rowIndex: tableLocation?.rowIndex,
              colIndex: tableLocation?.colIndex,
              rowLabel:
                tableLocation?.rowLabel ||
                chunkLabelMap.get(tv.lineItemId) ||
                dlView.title ||
                tv.lineItemId,
              axisLabels:
                tableLocation?.axisLabels ??
                tv.axisIds.map((axisId) => chunkLabelMap.get(axisId) ?? axisId),
              value: tableLocation?.value ?? tv.value,
              unitText: dlView.unitText,
              schemaType: tableLocation?.schemaType ?? tv.metadata.schemaType,
            });
          }
        }
      }
    }
  }

  return {
    identityKeyByNormalizedCellId,
    normalizedCellIdsByIdentityKey,
    identityLocationsByKey,
  };
};

interface CurrentCellIdentityLocationRef {
  uid?: string | null;
  normalizedCellId?: string | null;
}

const isCurrentCellIdentityLocation = (
  location: CellIdentityLocation,
  currentLocation: CurrentCellIdentityLocationRef | undefined,
): boolean => {
  if (!currentLocation) return false;
  if (currentLocation.uid && location.uid) {
    return location.uid === currentLocation.uid;
  }
  if (!location.uid && currentLocation.normalizedCellId) {
    return location.normalizedCellId === currentLocation.normalizedCellId;
  }
  return false;
};

export const getRelatedCellIdentityLocations = (
  identityKey: CellIdentityKey | null | undefined,
  identityLocationsByKey:
    | ReadonlyMap<CellIdentityKey, readonly CellIdentityLocation[]>
    | null
    | undefined,
  currentLocation?: CurrentCellIdentityLocationRef,
): CellIdentityLocation[] | undefined => {
  if (!identityKey) return undefined;
  return (identityLocationsByKey?.get(identityKey) ?? []).filter(
    (location) => !isCurrentCellIdentityLocation(location, currentLocation),
  );
};

export const getRelatedCellIdentityCount = (
  identityKey: CellIdentityKey | null | undefined,
  identityLocationsByKey:
    | ReadonlyMap<CellIdentityKey, readonly CellIdentityLocation[]>
    | null
    | undefined,
  currentLocation?: CurrentCellIdentityLocationRef,
): number | undefined =>
  getRelatedCellIdentityLocations(
    identityKey,
    identityLocationsByKey,
    currentLocation,
  )?.length;
